import type {JSX} from "react";
import {THEME} from "@/enums/profile";
import {BarDataKeyedType, BarDataType} from "@/utils/common/charts";
import {GetTheme} from "@/utils/common/general";
import {ResponsiveBar} from "@nivo/bar";

type BarProps<T_index extends string = "any", T_values extends string = "any"> = {
   data: (BarDataType | BarDataKeyedType<T_index, T_values>)[];
   keys?: string[];
   indexBy?: string;
   groupMode?: "grouped" | "stacked";
   label?: (d: any) => string;
   enableLabel?: boolean;
   tooltip?: (d: any) => string | JSX.Element;
   themeOverride?: THEME;
   padding?: number; // 0-1, higher values = thinner bars
};

export const Bar = <T_index extends string = "any", T_values extends string = "any">({
   data,
   keys,
   indexBy,
   groupMode,
   label,
   enableLabel = true,
   tooltip,
   themeOverride,
   padding = 0.3,
}: BarProps<T_index, T_values>) => {
   const theme = themeOverride || GetTheme();

   return (
      <ResponsiveBar
         data={data}
         keys={keys}
         indexBy={indexBy}
         groupMode={groupMode}
         label={label}
         enableLabel={enableLabel}
         tooltip={tooltip}
         padding={padding}
         margin={{top: 10, right: 0, bottom: 40, left: 50}}
         axisLeft={{
            tickValues: 5,
            format: (value: number) => {
               if (value >= 1000000) {
                  return `${value / 1000000}M`;
               }
               if (value >= 1000) {
                  return `${value / 1000}k`;
               }
               return value;
            },
         }}
         borderRadius={2}
         borderWidth={1}
         borderColor={{
            from: "color",
            modifiers: [["darker", 0.2]],
         }}
         colors={({
            id,
            data,
         }: {
            id: string | number;
            data: BarDataType | BarDataKeyedType<T_index, T_values>;
         }) => {
            if ("color" in data) {
               return data.color || "";
            }

            return `${id}Color` in data ? (data as any)[`${id}Color`] : "hsl(0, 0%, 0%)";
         }}
         theme={{
            axis: {
               ticks: {
                  line: {
                     strokeWidth: 0,
                  },
                  text: {
                     fill: theme === THEME.LIGHT ? "#9ca3af" : "#6b7280",
                  },
               },
            },
            grid: {
               line: {
                  stroke: theme === THEME.LIGHT ? "#f3f4f6" : "#1f2937",
                  strokeWidth: 1,
               },
            },
         }}
         labelTextColor={theme === THEME.LIGHT ? "#000" : "#fff"}
      />
   );
};
